/**
 * UA 检测工具（埋点专用）
 * 仅覆盖 PC / 移动浏览器 / 微信内置浏览器 三个环境，
 * 裁剪了原 Vue 版 SDK 中的 App 与各类小程序分支。
 * 所有访问 navigator 的位置均 guard 客户端，SSR 安全。
 */

const isBrowser = typeof window !== 'undefined';

function getUA(): string {
  return isBrowser ? window.navigator.userAgent : '';
}

/** 内核与终端检测结果 */
export interface UADetect {
  /** 是否为移动终端 */
  mobile: boolean;
  /** iOS 终端（iPhone） */
  ios: boolean;
  /** Android 终端 */
  android: boolean;
  /** iPad */
  iPad: boolean;
  /** 鸿蒙 */
  isHarmony: boolean;
  /** 微信内置浏览器 */
  weixin: boolean;
}

/** 解析 UA，返回终端检测结果（参照原 v() 方法裁剪） */
export function detect(): UADetect {
  const u = getUA().toLowerCase();
  return {
    mobile: !!getUA().match(/AppleWebKit.*Mobile.*/),
    ios: !!getUA().match(/\(i[^;]+;( U;)? CPU.+Mac OS X/),
    android: u.indexOf('android') > -1 || u.indexOf('linux') > -1,
    iPad: u.indexOf('ipad') > -1,
    isHarmony: /openharmony/.test(u),
    weixin: /micromessenger/i.test(u),
  };
}

/** 操作系统（参照原 os() 方法裁剪） */
export function os(): 'iOS' | 'Android' | 'iPad' | 'HarmonyOS' | 'PC' {
  const v = detect();
  if (v.ios) return 'iOS';
  if (v.android) return 'Android';
  if (v.iPad) return 'iPad';
  if (v.isHarmony) return 'HarmonyOS';
  return 'PC';
}

/**
 * 应用环境（参照原 app() 方法裁剪）
 * 本项目不在 App / 小程序内打开，只返回：
 * - Weixin：微信内置浏览器
 * - Mobile：其它移动浏览器
 * - PC：桌面浏览器
 */
export function app(): 'Weixin' | 'Mobile' | 'PC' {
  const v = detect();
  if (v.weixin) return 'Weixin';
  if (v.mobile) return 'Mobile';
  return 'PC';
}

/**
 * 终端类型（参照原 getTerminalId() 裁剪）
 * 0: wap, 1: web, 4: weixin
 */
export function getTerminalId(): 0 | 1 | 4 {
  const a = app();
  if (a === 'Weixin') return 4;
  if (a === 'Mobile') return 0;
  return 1;
}

export const isMobile = () => detect().mobile;
export const isWeixin = () => detect().weixin;
export const isiOS = () => detect().ios;
export const isAndroid = () => detect().android;
export const isIPad = () => detect().iPad;
export const isHarmony = () => detect().isHarmony;
